<template>
  <div class="service-order-charts p-2">
    <p class="font-weight-bold">{{ $t('service_orders.title') }}</p>
    <p class="text-muted">{{ $t('service_orders.charts.date_hint') }}</p>
    <p v-if="loading" role="status">{{ $t('service_orders.charts.loading') }}</p>
    <div v-else-if="error" role="alert">
      <p>{{ $t('service_orders.charts.load_error') }}</p>
      <button type="button" class="btn btn-primary" @click="loadOptions">
        {{ $t('service_orders.charts.retry') }}
      </button>
    </div>
    <template v-else>
      <label :for="`${chartId}-select`">{{ $t('service_orders.charts.select') }}</label>
      <select
        :id="`${chartId}-select`"
        v-model="selectedChart"
        class="form-control mb-2"
        :disabled="!chartOptions.length"
        @change="renderChart"
      >
        <option value="" disabled>{{ $t('service_orders.charts.select') }}</option>
        <option v-for="option in chartOptions" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
      <p v-if="!chartOptions.length">{{ $t('service_orders.charts.unavailable') }}</p>
    </template>
    <div v-show="selectedChart && !error" ref="chartHost" class="service-order-chart-host"></div>
  </div>
</template>

<script>
import { v4 as uuidv4 } from "uuid";
import OmniviewChartsSdk from "@omnicloud/charts-sdk";
import axios from "@/libs/axios";

export default {
  data() {
    return {
      chartId: `service-order-chart-${uuidv4()}`,
      fields: [],
      selectedChart: "",
      loading: false,
      error: false,
      chartsSdk: null,
      disposed: false,
    };
  },
  computed: {
    darkMode() {
      return this.$store.state.appConfig.layout.skin === "dark";
    },
    chartOptions() {
      return this.fields.reduce((options, field) => {
        if (!["both", "fields"].includes(field.visible_in)) return options;
        ["bar", "pie"].forEach(chart => {
          if (!(field.charts || []).includes(chart)) return;
          options.push({
            value: `${field.value}:${chart}`,
            field: field.value,
            chart,
            label: `${field.text} — ${this.$t(`service_orders.charts.${chart}`)}`,
          });
        });
        return options;
      }, []).concat(this.fields.some(field => (field.charts || []).includes("total")) ? [{
        value: "total",
        field: this.fields.find(field => (field.charts || []).includes("total")).value,
        chart: "total",
        label: this.$t("service_orders.charts.total"),
      }] : []);
    },
  },
  watch: {
    darkMode(value) {
      if (!this.chartsSdk) return;
      if (value) this.chartsSdk.setDarkMode();
      else this.chartsSdk.setLightMode();
    },
  },
  mounted() {
    this.loadOptions();
  },
  beforeDestroy() {
    this.disposed = true;
    this.destroyChart();
  },
  methods: {
    async loadOptions() {
      this.loading = true;
      this.error = false;
      try {
        const { data } = await axios.get("/v1/charts-api/get-config");
        if (this.disposed) return;
        const model = data.data.find(item => item.value === "ServiceOrder");
        this.fields = model ? model.fields : [];
      } catch (error) {
        if (!this.disposed) this.error = true;
      } finally {
        if (!this.disposed) this.loading = false;
      }
    },
    destroyChart() {
      if (this.chartsSdk) {
        this.chartsSdk.destroy();
        this.chartsSdk = null;
      }
      if (this.$refs.chartHost) this.$refs.chartHost.innerHTML = "";
    },
    async renderChart() {
      const option = this.chartOptions.find(item => item.value === this.selectedChart);
      if (!option) return;
      await this.$nextTick();
      if (this.disposed || this.selectedChart !== option.value) return;
      this.destroyChart();
      const target = document.createElement("div");
      target.id = this.chartId;
      this.$refs.chartHost.appendChild(target);
      this.chartsSdk = new OmniviewChartsSdk(`#${this.chartId}`, {
        token: window.localStorage.accessToken,
        apiUrl: `${window.__env.VUE_APP_BASE_URL}/v1`,
        eventMsUrl: window.__env.VUE_APP_MS_EVENTS_URL,
        gmapsKey: window.__env.VUE_APP_GMAPS_KEY,
        locale: this.$i18n.locale,
        darkMode: this.darkMode,
        chartData: {
          id: this.chartId,
          name: option.label,
          model: "ServiceOrder",
          field: option.field,
          chart: option.chart,
          data: {},
          filters: {},
          chartConf: {
            dateGroupFieldWildcard: "day",
            dateFilterWildcard: "month",
          },
        },
      });
      this.chartsSdk.init();
    },
  },
};
</script>

<style lang="scss" scoped>
.service-order-chart-host {
  position: relative;
  height: 70vh;
  min-height: 480px;
}
</style>
